import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from 'react';
import {
  Row,
  Col,
  Spin,
  message,
  Typography,
  Space,
  Card,
  Segmented,
  Input,
  Empty,
  Tag,
  Checkbox,
  Button,
  Alert,
  Tour,
  Modal,
} from 'antd';
import {
  SaveOutlined,
  PictureOutlined,
  LinkOutlined,
  SearchOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { v2ArticlesApi, publicSamplesApi } from '../../../../api';
import Editor from '../../../FactCheck/Editor';
import PexelsImageSearchImproved from './PexelsImageSearchImproved';
import ExportButtons from './ExportButtons';
import { useTutorialTour } from '../../../../hooks/useTutorialTour';
import { TOUR_CONFIGS } from '../../../../config/tourConfigs';
import HelpIcon from '../../../Common/HelpIcon';
import { generationStep4_4Help } from '../../../Common/helpContents';
import EnhancedTour from '../../../Common/EnhancedTour';
import './step4-editor-custom.css';
import './step4.css';

const { Title, Text } = Typography;

/**
 * STEP 4-4: 最終調整
 * - 左カラム: リッチテキストエディタ（軽微な修正）
 * - 右カラム: Pexels画像検索
 * - 下部: エクスポートボタン
 * - 自動保存機能
 */
const Step4_4_FinalAdjustment = ({
  articleUuid,
  articleData,
  initialCitations = [],
  isGuest = false,
}) => {
  const [loading, setLoading] = useState(true);
  const [editorData, setEditorData] = useState({ blocks: [] });
  const [images, setImages] = useState({});
  const [confirmedVersion, setConfirmedVersion] = useState(0);
  const [lastModified, setLastModified] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const editorInstanceRef = useRef(null); // Editor.jsインスタンスへの参照

  // ツアー管理
  const {
    tourOpen,
    completeTour,
    skipTour,
    restartTour,
    currentStep,
    updateCurrentStep,
    isMicroMode,
  } = useTutorialTour('generation_step4_4', {
    microKey: 'generation_step4_4_micro',
    disabled: isGuest,
  });

  // 親コンポーネントからのツアー再表示イベントをリッスン
  useEffect(() => {
    const handleRestartTour = () => restartTour();
    window.addEventListener('restartGenerationTour', handleRestartTour);
    return () =>
      window.removeEventListener('restartGenerationTour', handleRestartTour);
  }, [restartTour]);

  // 閲覧専用モード（status='completed'の場合）
  const isReadOnly = articleData?.status === 'completed';

  // 右カラムの表示切り替え
  const [rightPanelView, setRightPanelView] = useState('image');

  // 参考データ（citations）
  const [citations, setCitations] = useState([]);
  const [citationSearchQuery, setCitationSearchQuery] = useState('');
  const [highlightedCitationId, setHighlightedCitationId] = useState(null);

  // 引用番号表示の切り替え
  const [showCitationNumbers, setShowCitationNumbers] = useState(true);

  // 自動保存用の定期ポーリング
  const autoSaveIntervalRef = useRef(null);
  const hasUnsavedChangesRef = useRef(false);

  // 記事タイトル（Step4_3と同じロジック）
  const workingDraft =
    articleData?.outline?.outline_data?.working_draft || null;
  const articleTitle =
    workingDraft?.title ||
    articleData?.title ||
    articleData?.keyword ||
    '無題の記事';

  // sectionsをEditor.js形式に変換
  const convertSectionsToEditorBlocks = useCallback(
    (sections, imagesData = {}, includeTitle = true) => {
      const blocks = [];

      // タイトルを先頭に追加
      if (includeTitle && articleTitle) {
        blocks.push({
          type: 'header',
          data: {
            text: articleTitle,
            level: 1,
          },
        });
      }

      if (!sections || !Array.isArray(sections)) {
        console.warn('[Step4.4] sections is not an array:', sections);
        return { blocks };
      }

      sections.forEach(section => {
        const headingLevel =
          typeof section.heading_level === 'string'
            ? parseInt(section.heading_level.match(/\d+/)?.[0] || '0', 10)
            : section.heading_level || 0;
        const headingText = section.heading_text || '';
        const content = section.content || '';
        const headingId = section.heading_id || '';

        // リード文（__intro__）の場合
        if (headingId === '__intro__') {
          if (content) {
            let processedContent = content;

            // 改行削除（Step4.2と同じ）: H2, H3, H4, UL, TABLE の前後の\nと<br/>を削除
            processedContent = processedContent.replace(
              /(<br\s*\/?>|\n)+(\[H2\])/gi,
              '$2',
            );
            processedContent = processedContent.replace(
              /(\[\/H2\])(<br\s*\/?>|\n)+/gi,
              '$1',
            );
            processedContent = processedContent.replace(
              /(<br\s*\/?>|\n)+(\[H3\])/gi,
              '$2',
            );
            processedContent = processedContent.replace(
              /(\[\/H3\])(<br\s*\/?>|\n)+/gi,
              '$1',
            );
            processedContent = processedContent.replace(
              /(<br\s*\/?>|\n)+(\[H4\])/gi,
              '$2',
            );
            processedContent = processedContent.replace(
              /(\[\/H4\])(<br\s*\/?>|\n)+/gi,
              '$1',
            );
            processedContent = processedContent.replace(
              /(<br\s*\/?>|\n)+(\[UL\])/gi,
              '$2',
            );
            processedContent = processedContent.replace(
              /(\[\/UL\])(<br\s*\/?>|\n)+/gi,
              '$1',
            );
            processedContent = processedContent.replace(
              /(<br\s*\/?>|\n)+(\[TABLE\])/gi,
              '$2',
            );
            processedContent = processedContent.replace(
              /(\[\/TABLE\])(<br\s*\/?>|\n)+/gi,
              '$1',
            );

            // [H4]閉じタグ自動補完（Step4.2と同じ）
            processedContent = processedContent.replace(
              /\[H4\]([^\n]*?)(\n|$)/g,
              (match, content, lineEnd) => {
                if (content.includes('[/H4]')) {
                  return match;
                }
                return `[H4]${content}[/H4]${lineEnd}`;
              },
            );
            // [/H4]直後の改行も削除
            processedContent = processedContent.replace(
              /(\[\/H4\])(\n|<br\s*\/?>)/g,
              '$1',
            );

            // Markdownフォーマット変換（独自情報のテキストエディタ用、Step4.2と同じ）
            // 画像: ![alt](url) → <img src="url" alt="alt" />
            processedContent = processedContent.replace(
              /!\[([^\]]*)\]\(([^)]+)\)/g,
              '<img src="$2" alt="$1" style="max-width: 100%; height: auto; margin: 16px 0;" />',
            );
            // リンク: [text](url) → <a href="url">text</a> (引用番号[n]と誤マッチしないよう、URLを含む場合のみ)
            processedContent = processedContent.replace(
              /\[([^\]]+)\]\(([^)]+)\)/g,
              '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #1890ff; text-decoration: underline;">$1</a>',
            );
            // 見出し: # text → <h2>text</h2> (複数の#にも対応)
            processedContent = processedContent.replace(
              /^#{1}\s+(.+)$/gm,
              '<h2>$1</h2>',
            );
            processedContent = processedContent.replace(
              /^#{2}\s+(.+)$/gm,
              '<h3>$1</h3>',
            );
            processedContent = processedContent.replace(
              /^#{3}\s+(.+)$/gm,
              '<h4>$1</h4>',
            );
            // 箇条書き: - item → <ul><li>item</li></ul>
            processedContent = processedContent.replace(
              /(?:^- .+$\n?)+/gm,
              match => {
                const items = match
                  .trim()
                  .split('\n')
                  .map(line => {
                    const content = line.replace(/^-\s+/, '');
                    return `<li>${content}</li>`;
                  })
                  .join('');
                return `<ul>${items}</ul>`;
              },
            );

            // [H2][H3][H4]タグをHTML見出しに変換
            processedContent = processedContent.replace(
              /\[H2\](.*?)\[\/H2\]/g,
              '<h2>$1</h2>',
            );
            processedContent = processedContent.replace(
              /\[H3\](.*?)\[\/H3\]/g,
              '<h3>$1</h3>',
            );
            processedContent = processedContent.replace(
              /\[H4\](.*?)\[\/H4\]/g,
              '<h4>$1</h4>',
            );

            // <p>タグで囲まれた見出しタグを取り出す（HTMLが<p>で囲まれている場合の対応）
            processedContent = processedContent.replace(
              /<p>(<h[2-6]>.*?<\/h[2-6]>)<\/p>/g,
              '$1',
            );
            processedContent = processedContent.replace(
              /<p>(<ul>.*?<\/ul>)<\/p>/gs,
              '$1',
            );
            processedContent = processedContent.replace(
              /<p>(<table.*?<\/table>)<\/p>/gs,
              '$1',
            );

            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = processedContent;

            Array.from(tempDiv.children).forEach(element => {
              const tagName = element.tagName.toLowerCase();

              // H2-H6見出し
              if (/^h[2-6]$/.test(tagName)) {
                const subHeadingLevel = parseInt(tagName.substring(1));
                const subHeadingText = element.textContent.trim();
                blocks.push({
                  type: 'header',
                  data: {
                    text: subHeadingText,
                    level: subHeadingLevel,
                  },
                });
              }
              // 段落
              else if (tagName === 'p') {
                let text = element.innerHTML.trim();
                if (text) {
                  // [PEXELS_IMAGE:xxx] マーカーをpexelsImageブロックに変換
                  const markerMatch = text.match(/\[PEXELS_IMAGE:([^\]]+)\]/);
                  if (markerMatch) {
                    const markerId = markerMatch[1];
                    const imageData = imagesData[markerId];
                    if (imageData) {
                      blocks.push({
                        type: 'pexelsImage',
                        data: {
                          url: imageData.url,
                          alt: imageData.alt || '',
                          photographer: imageData.photographer,
                          photographer_url: imageData.photographer_url,
                          markerId: markerId,
                        },
                      });
                    } else {
                      console.warn(
                        '[Step4.4 LOAD] ⚠️ Image data not found for marker:',
                        markerId,
                      );
                      // マーカーだけ残す
                      blocks.push({
                        type: 'paragraph',
                        data: { text: text },
                      });
                    }
                  } else {
                    // 通常の段落
                    blocks.push({
                      type: 'paragraph',
                      data: { text: text },
                    });
                  }
                }
              } else if (tagName === 'ul' || tagName === 'ol') {
                const items = Array.from(element.querySelectorAll('li')).map(
                  li => li.innerHTML.trim(),
                );
                blocks.push({
                  type: 'list',
                  data: {
                    style: tagName === 'ol' ? 'ordered' : 'unordered',
                    items: items,
                  },
                });
              } else if (tagName === 'table') {
                // テーブルの処理（ヘッダー行対応）
                const tableRows = Array.from(element.querySelectorAll('tr'));
                const hasHeaders =
                  tableRows.length > 0 &&
                  tableRows[0].querySelectorAll('th').length > 0;

                const rows = tableRows.map(tr =>
                  Array.from(tr.querySelectorAll('td, th')).map(cell =>
                    cell.textContent.trim(),
                  ),
                );
                blocks.push({
                  type: 'table',
                  data: {
                    content: rows,
                    withHeadings: hasHeaders, // ⭐ ヘッダー行フラグを追加
                  },
                });
              }
            });
          }
          return;
        }

        // 見出しブロック（heading_levelが2以上の場合のみ）
        if (headingLevel >= 2 && headingText) {
          blocks.push({
            type: 'header',
            data: {
              text: headingText,
              level: headingLevel,
            },
          });
        }

        // コンテンツをHTMLからブロックに変換
        if (content) {
          let processedContent = content;

          // 改行削除（Step4.2と同じ）: H2, H3, H4, UL, TABLE の前後の\nと<br/>を削除
          processedContent = processedContent.replace(
            /(<br\s*\/?>|\n)+(\[H2\])/gi,
            '$2',
          );
          processedContent = processedContent.replace(
            /(\[\/H2\])(<br\s*\/?>|\n)+/gi,
            '$1',
          );
          processedContent = processedContent.replace(
            /(<br\s*\/?>|\n)+(\[H3\])/gi,
            '$2',
          );
          processedContent = processedContent.replace(
            /(\[\/H3\])(<br\s*\/?>|\n)+/gi,
            '$1',
          );
          processedContent = processedContent.replace(
            /(<br\s*\/?>|\n)+(\[H4\])/gi,
            '$2',
          );
          processedContent = processedContent.replace(
            /(\[\/H4\])(<br\s*\/?>|\n)+/gi,
            '$1',
          );
          processedContent = processedContent.replace(
            /(<br\s*\/?>|\n)+(\[UL\])/gi,
            '$2',
          );
          processedContent = processedContent.replace(
            /(\[\/UL\])(<br\s*\/?>|\n)+/gi,
            '$1',
          );
          processedContent = processedContent.replace(
            /(<br\s*\/?>|\n)+(\[TABLE\])/gi,
            '$2',
          );
          processedContent = processedContent.replace(
            /(\[\/TABLE\])(<br\s*\/?>|\n)+/gi,
            '$1',
          );

          // [H4]閉じタグ自動補完（Step4.2と同じ）
          processedContent = processedContent.replace(
            /\[H4\]([^\n]*?)(\n|$)/g,
            (match, content, lineEnd) => {
              if (content.includes('[/H4]')) {
                return match;
              }
              return `[H4]${content}[/H4]${lineEnd}`;
            },
          );
          // [/H4]直後の改行も削除
          processedContent = processedContent.replace(
            /(\[\/H4\])(\n|<br\s*\/?>)/g,
            '$1',
          );

          // Markdownフォーマット変換（独自情報のテキストエディタ用、Step4.2と同じ）
          // 画像: ![alt](url) → <img src="url" alt="alt" />
          processedContent = processedContent.replace(
            /!\[([^\]]*)\]\(([^)]+)\)/g,
            '<img src="$2" alt="$1" style="max-width: 100%; height: auto; margin: 16px 0;" />',
          );
          // リンク: [text](url) → <a href="url">text</a> (引用番号[n]と誤マッチしないよう、URLを含む場合のみ)
          processedContent = processedContent.replace(
            /\[([^\]]+)\]\(([^)]+)\)/g,
            '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #1890ff; text-decoration: underline;">$1</a>',
          );
          // 見出し: # text → <h2>text</h2> (複数の#にも対応)
          processedContent = processedContent.replace(
            /^#{1}\s+(.+)$/gm,
            '<h2>$1</h2>',
          );
          processedContent = processedContent.replace(
            /^#{2}\s+(.+)$/gm,
            '<h3>$1</h3>',
          );
          processedContent = processedContent.replace(
            /^#{3}\s+(.+)$/gm,
            '<h4>$1</h4>',
          );
          // 箇条書き: - item → <ul><li>item</li></ul>
          processedContent = processedContent.replace(
            /(?:^- .+$\n?)+/gm,
            match => {
              const items = match
                .trim()
                .split('\n')
                .map(line => {
                  const content = line.replace(/^-\s+/, '');
                  return `<li>${content}</li>`;
                })
                .join('');
              return `<ul>${items}</ul>`;
            },
          );

          // [H2][H3][H4]タグをHTML見出しに変換
          processedContent = processedContent.replace(
            /\[H2\](.*?)\[\/H2\]/g,
            '<h2>$1</h2>',
          );
          processedContent = processedContent.replace(
            /\[H3\](.*?)\[\/H3\]/g,
            '<h3>$1</h3>',
          );
          processedContent = processedContent.replace(
            /\[H4\](.*?)\[\/H4\]/g,
            '<h4>$1</h4>',
          );

          // <p>タグで囲まれた見出しタグを取り出す（HTMLが<p>で囲まれている場合の対応）
          processedContent = processedContent.replace(
            /<p>(<h[2-6]>.*?<\/h[2-6]>)<\/p>/g,
            '$1',
          );
          processedContent = processedContent.replace(
            /<p>(<ul>.*?<\/ul>)<\/p>/gs,
            '$1',
          );
          processedContent = processedContent.replace(
            /<p>(<table.*?<\/table>)<\/p>/gs,
            '$1',
          );

          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = processedContent;

          Array.from(tempDiv.children).forEach(element => {
            const tagName = element.tagName.toLowerCase();

            // H2-H6見出し
            if (/^h[2-6]$/.test(tagName)) {
              const subHeadingLevel = parseInt(tagName.substring(1));
              const subHeadingText = element.textContent.trim();
              blocks.push({
                type: 'header',
                data: {
                  text: subHeadingText,
                  level: subHeadingLevel,
                },
              });
            }
            // 段落
            else if (tagName === 'p') {
              let text = element.innerHTML.trim();
              if (text) {
                // [PEXELS_IMAGE:xxx] マーカーをpexelsImageブロックに変換
                const markerMatch = text.match(/\[PEXELS_IMAGE:([^\]]+)\]/);
                if (markerMatch) {
                  const markerId = markerMatch[1];
                  const imageData = imagesData[markerId];
                  if (imageData) {
                    blocks.push({
                      type: 'pexelsImage',
                      data: {
                        url: imageData.url,
                        alt: imageData.alt || '',
                        photographer: imageData.photographer,
                        photographer_url: imageData.photographer_url,
                        markerId: markerId,
                      },
                    });
                  } else {
                    console.warn(
                      '[Step4.4 LOAD] ⚠️ Image data not found for marker:',
                      markerId,
                    );
                    // マーカーだけ残す
                    blocks.push({
                      type: 'paragraph',
                      data: { text: text },
                    });
                  }
                } else {
                  // 通常の段落：短い段落（出典タグのみ）を検出し、前のブロックに統合
                  const plainText = text.replace(/<[^>]*>/g, '').trim();
                  const isCitationOnly =
                    plainText.length === 0 && /<sup>\[\d+\]<\/sup>/.test(text);

                  if (
                    isCitationOnly &&
                    blocks.length > 0 &&
                    blocks[blocks.length - 1].type === 'paragraph'
                  ) {
                    blocks[blocks.length - 1].data.text += text;
                  } else {
                    blocks.push({
                      type: 'paragraph',
                      data: { text: text },
                    });
                  }
                }
              }
            }
            // リスト
            else if (tagName === 'ul' || tagName === 'ol') {
              const items = Array.from(element.querySelectorAll('li')).map(li =>
                li.innerHTML.trim(),
              );
              if (items.length > 0) {
                blocks.push({
                  type: 'list',
                  data: {
                    style: tagName === 'ol' ? 'ordered' : 'unordered',
                    items: items,
                  },
                });
              }
            }
            // テーブル（ヘッダー行対応）
            else if (tagName === 'table') {
              const tableRows = Array.from(element.querySelectorAll('tr'));
              const hasHeaders =
                tableRows.length > 0 &&
                tableRows[0].querySelectorAll('th').length > 0;

              const rows = tableRows.map(tr =>
                Array.from(tr.querySelectorAll('td, th')).map(cell =>
                  cell.textContent.trim(),
                ),
              );
              if (rows.length > 0) {
                blocks.push({
                  type: 'table',
                  data: {
                    content: rows,
                    withHeadings: hasHeaders, // ⭐ ヘッダー行フラグを追加
                  },
                });
              }
            }
          });
        }
      });

      return { blocks };
    },
    [articleTitle],
  );

  // Editor.js形式からsectionsに逆変換
  const convertEditorBlocksToSections = useCallback(editorData => {
    const sections = [];
    let currentSection = null;
    const introBlocks = []; // リード文用
    let currentSectionId = '__intro__'; // 現在のセクションID（デフォルトはリード文）

    editorData.blocks.forEach((block, idx) => {
      if (block.type === 'header') {
        // H1（タイトル）はスキップ
        if (block.data.level === 1) {
          return;
        }

        // 現在のセクションを保存
        if (currentSection) {
          sections.push(currentSection);
        }

        // 新しいセクションを開始
        const newSectionId = `heading_${sections.length}`;
        currentSection = {
          heading_level: block.data.level,
          heading_text: block.data.text,
          heading_id: newSectionId,
          content: '',
        };
        currentSectionId = newSectionId;
      } else {
        // コンテンツブロックの処理
        let html = '';

        if (block.type === 'paragraph') {
          let text = block.data.text || '';

          // [PEXELS_IMAGE:xxx] マーカーを検出して保持
          if (text.includes('[PEXELS_IMAGE:')) {
            const markerMatch = text.match(/\[PEXELS_IMAGE:([^\]]+)\]/);
            if (markerMatch) {
              const markerId = markerMatch[1];
              // マーカーをそのまま保持
              html = `<p>${text}</p>`;
            } else {
              console.warn('[Step4.4 SAVE] ⚠️ Marker pattern not matched');
              html = `<p>${text}</p>`;
            }
          } else {
            // Editor.jsが削除した<sup>を復元: [n] → <sup>[n]</sup>
            // ただし既に<sup>の場合はそのまま
            text = text.replace(
              /(?<!<sup>)\[(\d+)\](?!<\/sup>)/g,
              '<sup>[$1]</sup>',
            );

            html = `<p>${text}</p>`;
          }
        } else if (block.type === 'list') {
          // itemsが配列であることを確認
          const items = (block.data.items || [])
            .map(item => {
              // itemの型を確認してHTML文字列を取得
              let itemHtml = '';

              if (typeof item === 'string') {
                itemHtml = item;
              } else if (typeof item === 'object' && item !== null) {
                // Editor.jsのlistツールはcontentプロパティを持つオブジェクトを返す
                itemHtml = item.content || item.text || '';
              } else {
                itemHtml = String(item || '');
              }

              // 空の場合はスキップ
              if (!itemHtml) return '';

              // Editor.jsが削除した<sup>を復元: [n] → <sup>[n]</sup>
              itemHtml = itemHtml.replace(
                /(?<!<sup>)\[(\d+)\](?!<\/sup>)/g,
                '<sup>[$1]</sup>',
              );

              return `<li>${itemHtml}</li>`;
            })
            .filter(item => item) // 空のアイテムを除外
            .join('');
          html = `<ul>${items}</ul>`;
        } else if (block.type === 'table') {
          const withHeadings = block.data.withHeadings || false;
          const tableContent = block.data.content || [];

          const rows = tableContent
            .map((row, rowIndex) => {
              const isHeaderRow = withHeadings && rowIndex === 0;
              const cellTag = isHeaderRow ? 'th' : 'td';
              const cells = row
                .map(cell => `<${cellTag}>${cell}</${cellTag}>`)
                .join('');
              return `<tr>${cells}</tr>`;
            })
            .join('');
          html = `<table>${rows}</table>`;
        } else if (block.type === 'pexelsImage') {
          // Pexels画像ブロック：マーカーIDを保存
          const markerId = block.data.markerId || '';
          if (markerId) {
            html = `<p>[PEXELS_IMAGE:${markerId}]</p>`;
          } else {
            console.warn(
              '[Step4.4 SAVE] ⚠️ pexelsImage block without markerId',
            );
          }
        }

        if (currentSection) {
          // セクションにコンテンツを追加
          currentSection.content += html;
        } else {
          // まだ見出しがない場合はリード文として扱う
          introBlocks.push(html);
        }
      }
    });

    // 最後のセクションを保存
    if (currentSection) {
      sections.push(currentSection);
    }

    // リード文がある場合は先頭に追加
    if (introBlocks.length > 0) {
      sections.unshift({
        heading_level: 0,
        heading_text: '',
        heading_id: '__intro__',
        content: introBlocks.join(''),
      });
    }

    return sections;
  }, []);

  // ドラフトデータ読み込み
  const loadDraftData = useCallback(async () => {
    try {
      setLoading(true);
      const articleApi = isGuest ? publicSamplesApi : v2ArticlesApi;
      const data = await articleApi.getStep4Draft(articleUuid);

      // blocksが空の場合の警告
      if (!data.blocks || data.blocks.length === 0) {
        console.warn('[Step4.4] No blocks found in draft data.');
        message.warning({
          content:
            'ドラフトデータが空です。STEP4-3で原稿を確定してから進んでください。',
          duration: 5,
        });
      }

      // タイトルをH1として先頭に追加
      const blocks = data.blocks || [];
      if (
        articleTitle &&
        (blocks.length === 0 ||
          blocks[0]?.type !== 'header' ||
          blocks[0]?.data?.level !== 1)
      ) {
        blocks.unshift({
          type: 'header',
          data: {
            text: articleTitle,
            level: 1,
          },
        });
      }

      setEditorData({ blocks });
      setImages(data.images || {});
      setConfirmedVersion(data.confirmed_version || 0);
      setLastModified(data.last_modified);

      // 参考データ（citations）を取得
      // propsから渡された場合はそれを使用、なければ専用APIから取得
      if (initialCitations && initialCitations.length > 0) {
        setCitations(initialCitations);
      } else {
        try {
          const citationsData =
            await articleApi.getStep4Citations(articleUuid);
          setCitations(citationsData.citations || []);
        } catch (citationError) {
          console.warn('[Step4.4] Failed to load citations:', citationError);
          setCitations([]);
        }
      }

      setLoading(false);
    } catch (error) {
      console.error('[Step4.4] Failed to load draft data:', error);
      console.error('[Step4.4] Error details:', error.response?.data?.detail);
      setLoading(false);
      message.error('ドラフトデータの読み込みに失敗しました');
    }
  }, [articleUuid, initialCitations, isGuest]);

  // 初回データ読み込み
  useEffect(() => {
    loadDraftData();
  }, []); // 空の依存配列で1回だけ実行

  // STEP4.4到達時のEXP付与（フォールバック: handleRefinementCompletedを経由しなかった場合に備える）
  useEffect(() => {
    if (!articleUuid || isReadOnly || isGuest) return;
    const grantExp = async () => {
      try {
        await v2ArticlesApi.arriveStep44(articleUuid);
      } catch (e) {
        // already granted or error - silent
      }
    };
    grantExp();
  }, [articleUuid, isReadOnly, isGuest]);

  // 30秒ごとの自動保存ポーリング
  useEffect(() => {
    const saveIfNeeded = async () => {
      if (!hasUnsavedChangesRef.current) {
        return;
      }

      try {
        setAutoSaving(true);
        // Editor.jsから最新データを取得
        let latestBlocks = [];
        if (editorInstanceRef.current && editorInstanceRef.current.save) {
          const savedData = await editorInstanceRef.current.save();
          latestBlocks = savedData.blocks;

          // Editor.jsが削除した<sup>を復元: [n] → <sup>[n]</sup>
          latestBlocks = latestBlocks.map(block => {
            if (block.type === 'paragraph' && block.data.text) {
              return {
                ...block,
                data: {
                  ...block.data,
                  text: block.data.text.replace(
                    /(?<!<sup>)\[(\d+)\](?!<\/sup>)/g,
                    '<sup>[$1]</sup>',
                  ),
                },
              };
            } else if (block.type === 'list' && block.data.items) {
              return {
                ...block,
                data: {
                  ...block.data,
                  items: block.data.items.map(item => {
                    const itemText =
                      typeof item === 'string'
                        ? item
                        : item.content || item.text || '';
                    return itemText.replace(
                      /(?<!<sup>)\[(\d+)\](?!<\/sup>)/g,
                      '<sup>[$1]</sup>',
                    );
                  }),
                },
              };
            }
            return block;
          });
        } else {
          console.warn('[Step4.4] Editor instance not available for save');
          return;
        }

        // imagesとconfirmed_versionも最新の状態を取得
        const latestImages = await new Promise(resolve => {
          setImages(current => {
            resolve(current);
            return current;
          });
        });

        const latestConfirmedVersion = await new Promise(resolve => {
          setConfirmedVersion(current => {
            resolve(current);
            return current;
          });
        });

        await v2ArticlesApi.updateStep4Draft(articleUuid, {
          blocks: latestBlocks,
          images: latestImages,
          confirmed_version: latestConfirmedVersion,
        });

        hasUnsavedChangesRef.current = false;
        setLastModified(new Date().toISOString());
      } catch (error) {
        console.error('[Step4.4] ❌ Auto-save failed:', error);
      } finally {
        setAutoSaving(false);
      }
    };

    // 30秒ごとにチェック
    autoSaveIntervalRef.current = setInterval(saveIfNeeded, 30000);

    // クリーンアップ
    return () => {
      if (autoSaveIntervalRef.current) {
        clearInterval(autoSaveIntervalRef.current);
      }
      // アンマウント時に未保存の変更があれば保存を試みる
      if (hasUnsavedChangesRef.current) {
        saveIfNeeded();
      }
    };
  }, [articleUuid, convertEditorBlocksToSections]); // sectionsとimagesとconfirmedVersionを削除

  // エディタ変更ハンドラー（シンプルに状態更新のみ）
  const lastProcessedDataRef = useRef(null);

  const handleEditorChange = useCallback(
    newEditorData => {
      if (!newEditorData || !newEditorData.blocks) {
        return;
      }

      // データのシリアライズ化して比較（深い比較）
      const dataStr = JSON.stringify(newEditorData);

      // 前回と全く同じデータの場合はスキップ
      if (lastProcessedDataRef.current === dataStr) {
        return;
      }

      lastProcessedDataRef.current = dataStr;

      setEditorData(newEditorData);

      // 未保存フラグを立てる（30秒後のポーリングで保存される）
      // sectionsへの変換はここでは行わない（保存時のみ）
      hasUnsavedChangesRef.current = true;
    },
    [], // 依存配列を空にして安定化
  );

  // 画像追加ハンドラー
  const handleImageAdd = useCallback((sectionId, imageData) => {
    setImages(prev => {
      const sectionImages = prev[sectionId] || [];
      return {
        ...prev,
        [sectionId]: [...sectionImages, imageData],
      };
    });
    hasUnsavedChangesRef.current = true;
  }, []);

  // 画像削除ハンドラー
  const handleImageRemove = useCallback((sectionId, imageIndex) => {
    setImages(prev => {
      const sectionImages = prev[sectionId] || [];
      const newImages = sectionImages.filter((_, idx) => idx !== imageIndex);
      return {
        ...prev,
        [sectionId]: newImages,
      };
    });
    hasUnsavedChangesRef.current = true;
  }, []);

  // 画像選択ハンドラー（ドラッグ&ドロップ用）
  const handleImageInsert = useCallback(async imageData => {
    try {
      // Editor.jsのインスタンスに直接ブロックを追加
      if (editorInstanceRef.current && editorInstanceRef.current.blocks) {
        // ユニークなマーカーIDを生成
        const markerId = `pexels-img-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // 現在のカーソル位置の次に挿入
        const currentIndex =
          editorInstanceRef.current.blocks.getCurrentBlockIndex();
        // カスタムブロックとして挿入
        await editorInstanceRef.current.blocks.insert(
          'pexelsImage',
          {
            url: imageData.url,
            alt: imageData.alt || '',
            photographer: imageData.photographer,
            photographer_url: imageData.photographer_url,
            markerId: markerId,
          },
          {},
          currentIndex + 1,
          true,
        );

        // imagesステートに保存（マーカーIDをキーとして）
        setImages(prev => {
          return {
            ...prev,
            [markerId]: {
              ...imageData,
              markerId: markerId,
            },
          };
        });
      } else {
        console.warn('[Step4.4] Editor instance not ready');
      }

      hasUnsavedChangesRef.current = true;
      message.success('画像を挿入しました');
    } catch (error) {
      console.error('[Step4.4] Failed to insert image:', error);
      message.error('画像の挿入に失敗しました');
    }
  }, []);

  // Editor.js準備完了ハンドラー
  const handleEditorReady = useCallback(() => {
    if (window._editor) {
      editorInstanceRef.current = window._editor;
      // <sup>タグをクリッカブルにする
      setTimeout(() => {
        const editorContainer = document.querySelector('.codex-editor');
        if (editorContainer) {
          const supElements = editorContainer.querySelectorAll('sup');

          supElements.forEach(sup => {
            // 引用番号を抽出
            const match = sup.textContent.match(/\[(\d+)\]/);
            if (match) {
              const citationId = match[1];
              sup.setAttribute('data-citation-id', citationId);
              // step4-citation-numberクラスを追加（ドキュメントレベルのクリックイベントで検出するため）
              sup.classList.add('step4-citation-number');
              // スタイルを追加
              sup.style.cursor = 'pointer';
              sup.style.color = '#1890ff';
              sup.style.fontWeight = '600';
              sup.style.textDecoration = 'underline';
            }
          });
        }
      }, 1000);
    }
  }, [setHighlightedCitationId, setRightPanelView]);

  // 参考データの挿入ハンドラー
  const handleCitationInsert = useCallback(async citation => {
    try {
      // Editor.jsのインスタンスに直接ブロックを追加
      if (editorInstanceRef.current && editorInstanceRef.current.blocks) {
        const citationUrl =
          citation.url || citation.source_url || citation.link || '';

        let insertText = '';
        if (citationUrl && citationUrl !== 'provided_info') {
          // URLがある場合：「参考：URL全文（リンク）」
          insertText = `参考：<a href="${citationUrl}" target="_blank" rel="noopener noreferrer">${citationUrl}</a>`;
        } else {
          // URLがない（独自情報）場合：テキストのみ
          const fullText = citation.content || citation.text || '';
          insertText = `参考：${fullText}`;
        }

        await editorInstanceRef.current.blocks.insert('paragraph', {
          text: insertText,
        });

        hasUnsavedChangesRef.current = true;
        message.success('参考情報を挿入しました');
      } else {
        console.warn('[Step4.4] Editor instance not ready');
      }
    } catch (error) {
      console.error('[Step4.4] Failed to insert citation:', error);
      message.error('参考情報の挿入に失敗しました');
    }
  }, []);

  // URLからドメインを抽出
  const extractDomain = url => {
    if (!url) return '';
    try {
      const urlObj = new URL(url);
      return urlObj.hostname.replace('www.', '');
    } catch (e) {
      return url;
    }
  };

  // 引用番号表示の切り替え（CSSクラスで制御）
  useEffect(() => {
    const editorElement = document.querySelector('.step4-4-final-adjustment');
    if (!editorElement) return;

    // showCitationNumbersの状態に応じてCSSクラスを切り替えるだけ
    // hide-citationsクラスはCSSで sup[data-citation-id] { display: none } を適用
  }, [showCitationNumbers]);

  // 引用番号クリック時のハンドラ
  const handleCitationLinkClick = useCallback(citationId => {
    // 1. 参考データビューに切り替え
    setRightPanelView('reference');

    // 2. 該当カードまでスクロール（少し遅延させてビュー切り替え後に実行）
    setTimeout(() => {
      const cardElement = document.getElementById(`citation-${citationId}`);
      if (cardElement) {
        cardElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        // 3. ハイライト表示
        setHighlightedCitationId(citationId);

        // 4. 3秒後にハイライトを解除
        setTimeout(() => {
          setHighlightedCitationId(null);
        }, 3000);
      }
    }, 100);
  }, []);

  // 引用番号クリック時の処理
  useEffect(() => {
    const handleCitationClick = e => {
      if (e.target.classList.contains('step4-citation-number')) {
        const citationId = parseInt(
          e.target.getAttribute('data-citation-id'),
          10,
        );
        handleCitationLinkClick(citationId);
      }
    };

    document.addEventListener('click', handleCitationClick);
    return () => document.removeEventListener('click', handleCitationClick);
  }, [handleCitationLinkClick]);

  // <sup>タグを監視してクリッカブルにする（Editor.jsのコンテンツ変更に対応）
  useEffect(() => {
    let observer = null;
    let intervalId = null;

    const setupCitationNumbers = () => {
      const editorContainer = document.querySelector('.codex-editor');
      if (editorContainer) {
        const supElements = editorContainer.querySelectorAll(
          'sup:not(.step4-citation-number)',
        );
        supElements.forEach(sup => {
          const match = sup.textContent.match(/\[(\d+)\]/);
          if (match) {
            const citationId = match[1];
            sup.setAttribute('data-citation-id', citationId);
            sup.classList.add('step4-citation-number');
            sup.style.cursor = 'pointer';
            sup.style.color = '#1890ff';
            sup.style.fontWeight = '600';
            sup.style.textDecoration = 'underline';
          }
        });
      }
    };

    // MutationObserverの設定
    const setupObserver = () => {
      const editorContainer = document.querySelector('.codex-editor');
      if (editorContainer && !observer) {
        observer = new MutationObserver(() => {
          // debounce: 短時間に大量の変更があっても1回だけ実行
          setTimeout(setupCitationNumbers, 100);
        });
        observer.observe(editorContainer, {
          childList: true,
          subtree: true,
          characterData: true, // テキスト変更も監視
        });
      }
    };

    // 初期セットアップ（複数回試行）
    const timer1 = setTimeout(setupCitationNumbers, 500);
    const timer2 = setTimeout(setupCitationNumbers, 1000);
    const timer3 = setTimeout(setupCitationNumbers, 2000);

    // MutationObserverを設定（editorContainerが存在するまで待機）
    const observerTimer = setTimeout(setupObserver, 1000);

    // 定期的にチェック（Editor.jsが再レンダリングした場合に対応）
    intervalId = setInterval(setupCitationNumbers, 3000);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      clearTimeout(observerTimer);
      clearInterval(intervalId);
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  // 一時保存（手動）
  const handleManualSave = useCallback(async () => {
    try {
      message.loading({
        content: '保存しています...',
        key: 'manualSave',
        duration: 0,
      });

      // Editor.jsから最新データを取得
      let latestBlocks = [];
      if (editorInstanceRef.current && editorInstanceRef.current.save) {
        const savedData = await editorInstanceRef.current.save();
        latestBlocks = savedData.blocks;

        // Editor.jsが削除した<sup>を復元: [n] → <sup>[n]</sup>
        latestBlocks = latestBlocks.map(block => {
          if (block.type === 'paragraph' && block.data.text) {
            return {
              ...block,
              data: {
                ...block.data,
                text: block.data.text.replace(
                  /(?<!<sup>)\[(\d+)\](?!<\/sup>)/g,
                  '<sup>[$1]</sup>',
                ),
              },
            };
          } else if (block.type === 'list' && block.data.items) {
            return {
              ...block,
              data: {
                ...block.data,
                items: block.data.items.map(item => {
                  const itemText =
                    typeof item === 'string'
                      ? item
                      : item.content || item.text || '';
                  return itemText.replace(
                    /(?<!<sup>)\[(\d+)\](?!<\/sup>)/g,
                    '<sup>[$1]</sup>',
                  );
                }),
              },
            };
          }
          return block;
        });
      } else {
        message.warning({
          content: 'エディタの準備ができていません',
          key: 'manualSave',
          duration: 2,
        });
        return;
      }

      await v2ArticlesApi.updateStep4Draft(articleUuid, {
        blocks: latestBlocks,
        images: images,
        confirmed_version: confirmedVersion,
      });

      hasUnsavedChangesRef.current = false;
      setLastModified(new Date().toISOString());

      message.success({
        content: '保存しました',
        key: 'manualSave',
        duration: 2,
      });
    } catch (error) {
      console.error('[Step4.4] Manual save failed:', error);
      message.error({
        content: '保存に失敗しました',
        key: 'manualSave',
        duration: 3,
      });
    }
  }, [articleUuid, images, confirmedVersion]);

  // 原稿を確定する（実際の処理）
  const executeConfirmDraft = async () => {
    try {
      message.loading({
        content: '閲覧モードに切り替えています...',
        key: 'confirmDraft',
        duration: 0,
      });

      // 1. 最終保存を実行
      let latestBlocks = [];
      if (editorInstanceRef.current && editorInstanceRef.current.save) {
        const savedData = await editorInstanceRef.current.save();
        latestBlocks = savedData.blocks;

        // Editor.jsが削除した<sup>を復元
        latestBlocks = latestBlocks.map(block => {
          if (block.type === 'paragraph' && block.data.text) {
            return {
              ...block,
              data: {
                ...block.data,
                text: block.data.text.replace(
                  /(?<!<sup>)\[(\d+)\](?!<\/sup>)/g,
                  '<sup>[$1]</sup>',
                ),
              },
            };
          } else if (block.type === 'list' && block.data.items) {
            return {
              ...block,
              data: {
                ...block.data,
                items: block.data.items.map(item => {
                  const itemText =
                    typeof item === 'string'
                      ? item
                      : item.content || item.text || '';
                  return itemText.replace(
                    /(?<!<sup>)\[(\d+)\](?!<\/sup>)/g,
                    '<sup>[$1]</sup>',
                  );
                }),
              },
            };
          }
          return block;
        });
      }

      await v2ArticlesApi.updateStep4Draft(articleUuid, {
        blocks: latestBlocks,
        images: images,
        confirmed_version: confirmedVersion,
      });

      // 2. status='completed' & current_step='4.4'に更新
      await v2ArticlesApi.finalizeArticle(articleUuid);

      hasUnsavedChangesRef.current = false;

      message.success({
        content: '閲覧モードに切り替えました',
        key: 'confirmDraft',
        duration: 3,
      });

      // ページをリロードして閲覧専用モードを反映
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error('[Step4.4] Confirmation failed:', error);
      message.error({
        content: `原稿の確定に失敗しました: ${error.response?.data?.detail || error.message}`,
        key: 'confirmDraft',
        duration: 3,
      });
    }
  };

  // 原稿を確定する（確認モーダルを表示）
  const handleConfirmDraft = () => {
    Modal.confirm({
      title: '閲覧モードに切り替えますか？',
      content: (
        <div>
          <p>
            閲覧モードに切り替えると、
            <strong>記事の編集ができなくなります</strong>（元に戻せません）。
          </p>
          <ul style={{ paddingLeft: 20, marginTop: 8 }}>
            <li>エクスポートや閲覧は引き続き可能です</li>
            <li>ステップを遡って各プロセスを確認できるようになります</li>
            <li>編集を続けたい場合は「キャンセル」を選択してください</li>
          </ul>
        </div>
      ),
      okText: '切り替える',
      cancelText: 'キャンセル',
      onOk: executeConfirmDraft,
    });
  };

  // 目次データを抽出（Editor.jsのブロックから）
  const extractTableOfContents = useCallback(() => {
    const toc = [];

    if (!editorData?.blocks) {
      return [];
    }

    editorData.blocks.forEach((block, index) => {
      if (block.type === 'header' && block.data.level >= 2) {
        toc.push({
          id: `heading-${index}`,
          level: `h${block.data.level}`,
          text: block.data.text || '(無題)',
        });
      }
    });

    return toc;
  }, [editorData]);

  // 最終更新時刻のフォーマット（日本時間）
  const formatLastModified = isoString => {
    if (!isoString) return '';
    try {
      const date = new Date(isoString);
      return date.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' });
    } catch {
      return '';
    }
  };

  // ツアーステップ定義（micro/fullを切り替え）
  const tourSteps = useMemo(() => {
    const configKey = isMicroMode
      ? 'generation_step4_4_micro'
      : 'generation_step4_4';
    if (!TOUR_CONFIGS[configKey]?.steps) return [];

    return TOUR_CONFIGS[configKey].steps.map(step => ({
      ...step,
      target: step.target ? step.target() : null,
    }));
  }, [tourOpen, isMicroMode]);

  if (loading) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '80px 20px',
          minHeight: '400px',
        }}
      >
        <Spin size="large" />
        <div style={{ marginTop: 24, fontSize: 16, color: '#666' }}>
          ドラフトを読み込んでいます...
        </div>
      </div>
    );
  }

  return (
    <div
      className={`step4-4-final-adjustment ${!showCitationNumbers ? 'hide-citations' : ''}`}
    >
      {/* ヘッダー：タイトル・説明なし、薄いグレー背景 */}
      <Card
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          marginBottom: 24,
          background: '#fafafa',
          border: 'none',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        }}
        bodyStyle={{ padding: '16px 24px' }}
      >
        <div
          className="step4-4-header-actions"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          {/* 左側：自動保存ステータス + 一時保存ボタン */}
          <Space size={8}>
            <div style={{ fontSize: 13, color: '#999' }}>
              {autoSaving && (
                <Space size={6}>
                  <SaveOutlined spin />
                  <span>保存中...</span>
                </Space>
              )}
              {!autoSaving && lastModified && (
                <span>最終保存: {formatLastModified(lastModified)}</span>
              )}
            </div>
            {/* 一時保存ボタン */}
            <Button
              size="small"
              icon={<SaveOutlined />}
              onClick={handleManualSave}
              disabled={autoSaving || isReadOnly}
            >
              一時保存
            </Button>
          </Space>

          {/* 右側：操作ボタン群 */}
          <Space size={8}>
            {/* 引用番号表示切り替え */}
            <Space size={4}>
              <Checkbox
                checked={showCitationNumbers}
                onChange={e => setShowCitationNumbers(e.target.checked)}
                style={{ fontSize: 13 }}
              >
                引用番号を表示
              </Checkbox>
              <HelpIcon
                tooltip={generationStep4_4Help.citations.tooltip}
                title={generationStep4_4Help.citations.title}
                content={generationStep4_4Help.citations.content}
              />
            </Space>

            {/* 閲覧モードに切り替える */}
            <Space size={4}>
              <Button
                type="default"
                size="small"
                icon={<CheckOutlined />}
                onClick={handleConfirmDraft}
                disabled={isReadOnly}
              >
                閲覧モードに切り替える
              </Button>
              <HelpIcon
                tooltip={generationStep4_4Help.complete.tooltip}
                title={generationStep4_4Help.complete.title}
                content={generationStep4_4Help.complete.content}
              />
            </Space>

            {/* エクスポートボタン */}
            <ExportButtons
              uuid={articleUuid}
              blocks={editorData.blocks}
              images={images}
              articleTitle={articleTitle}
              showCitations={showCitationNumbers}
              citations={citations}
              size="small"
              articleCompleted={isReadOnly}
              onRequestComplete={executeConfirmDraft}
            />
          </Space>
        </div>
      </Card>

      {/* メインコンテンツ: 2カラムレイアウト */}
      <Row gutter={24} className="step4-4-layout">
        {/* 左カラム: Editor.js */}
        <Col xs={24} lg={16}>
          <div
            style={{ minHeight: '500px' }}
            onDragOver={e => {
              if (isReadOnly) return; // 閲覧専用時はドロップを無効化
              e.preventDefault();
              e.dataTransfer.dropEffect = 'copy';
            }}
            onDrop={e => {
              if (isReadOnly) return; // 閲覧専用時はドロップを無効化
              e.preventDefault();

              // 画像データのドロップ
              const imageData = e.dataTransfer.getData('image');
              if (imageData) {
                try {
                  const image = JSON.parse(imageData);
                  handleImageInsert(image);
                } catch (error) {
                  console.error('画像データのパースに失敗:', error);
                }
                return;
              }

              // 参考データのドロップ
              const citationData = e.dataTransfer.getData('citation');
              if (citationData) {
                try {
                  const citation = JSON.parse(citationData);
                  handleCitationInsert(citation);
                } catch (error) {
                  console.error('参考データのパースに失敗:', error);
                }
                return;
              }
            }}
          >
            <Editor
              initialData={editorData}
              onChange={handleEditorChange}
              onReady={handleEditorReady}
              config={{
                holder: 'step4-editor',
                minHeight: 0,
                placeholder: '本文を編集...',
              }}
            />
          </div>
        </Col>

        {/* 右カラム: 画像検索 / 参考データ */}
        <Col xs={24} lg={8}>
          <div
            style={{
              position: 'sticky',
              top: 80,
              maxHeight: 'calc(100vh - 96px)',
              background: '#fafafa',
              borderRadius: '8px',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* Segmented for switching between image and reference */}
            <div style={{ marginBottom: 16, flexShrink: 0 }}>
              <Segmented
                value={rightPanelView}
                onChange={value => setRightPanelView(value)}
                options={[
                  {
                    label: '画像挿入',
                    value: 'image',
                    icon: <PictureOutlined />,
                  },
                  {
                    label: `参考データ (${citations.length})`,
                    value: 'reference',
                    icon: <LinkOutlined />,
                  },
                ]}
                block
              />
            </div>

            {/* スクロール可能なボディ部分 */}
            <div
              style={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Image View */}
              <div
                style={{
                  display: rightPanelView === 'image' ? 'block' : 'none',
                  height: '100%',
                }}
              >
                {isGuest ? (
                  <Empty description="画像検索はログイン後にご利用いただけます" style={{ padding: '40px 0' }} />
                ) : (
                  <PexelsImageSearchImproved
                    articleKeyword={articleData?.keyword || ''}
                  />
                )}
              </div>

              {/* Reference Data View */}
              <div
                style={{
                  display: rightPanelView === 'reference' ? 'block' : 'none',
                  flex: 1,
                  overflowY: 'auto',
                  minHeight: 0,
                }}
              >
                {/* 検索ボックス */}
                {citations.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <Input
                      placeholder="参考データを検索..."
                      prefix={<SearchOutlined />}
                      value={citationSearchQuery}
                      onChange={e => setCitationSearchQuery(e.target.value)}
                      allowClear
                      style={{ borderRadius: 6 }}
                    />
                  </div>
                )}

                {citations.length === 0 ? (
                  <Empty
                    description="参考データなし"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                ) : (
                  <div>
                    {/* citation_idでソート、検索フィルター適用 */}
                    {[...citations]
                      .filter(citation => {
                        if (!citationSearchQuery) return true;
                        const query = citationSearchQuery.toLowerCase();
                        const content = (
                          citation.content ||
                          citation.text ||
                          ''
                        ).toLowerCase();
                        return content.includes(query);
                      })
                      .sort(
                        (a, b) => (a.citation_id || 0) - (b.citation_id || 0),
                      )
                      .map((citation, index) => {
                        // provided_infoかどうかをチェック
                        const isProvidedInfo =
                          citation.source_url === 'provided_info' ||
                          citation.url === 'provided_info' ||
                          (!citation.url &&
                            !citation.source_url &&
                            !citation.link);

                        // URLの取得
                        const citationUrl =
                          !isProvidedInfo &&
                          (citation.url ||
                            citation.source_url ||
                            citation.link ||
                            '');

                        // テキストを100文字で切る
                        const fullText =
                          citation.content || citation.text || '';
                        const truncatedText =
                          fullText.length > 100
                            ? fullText.substring(0, 100) + '…'
                            : fullText;

                        return (
                          <div
                            key={citation.citation_id || index}
                            id={`citation-${citation.citation_id || citation.id}`}
                            className={
                              highlightedCitationId ===
                              (citation.citation_id || citation.id)
                                ? 'citation-card-highlight'
                                : 'citation-card-normal'
                            }
                            draggable
                            onDragStart={e => {
                              e.dataTransfer.setData(
                                'citation',
                                JSON.stringify(citation),
                              );
                              e.dataTransfer.effectAllowed = 'copy';
                            }}
                            style={{
                              padding: '16px 12px',
                              borderBottom: '1px solid #f0f0f0',
                              cursor: 'move',
                            }}
                          >
                            {/* 引用番号 + エビデンステキスト */}
                            <div
                              style={{
                                display: 'flex',
                                gap: 12,
                                marginBottom: 8,
                              }}
                            >
                              {/* 左側：引用番号 */}
                              <div
                                style={{
                                  fontSize: 13,
                                  fontWeight: '600',
                                  color: '#8c8c8c',
                                  flexShrink: 0,
                                  minWidth: 20,
                                }}
                              >
                                {citation.citation_id || citation.id}:
                              </div>

                              {/* 右側：エビデンステキスト */}
                              {(citation.content || citation.text) && (
                                <div
                                  style={{
                                    flex: 1,
                                    fontSize: 13,
                                    lineHeight: 1.6,
                                    color: '#333',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                  }}
                                  dangerouslySetInnerHTML={{
                                    __html: truncatedText.replace(
                                      /\*\*(.*?)\*\*/g,
                                      '<strong style="font-weight: 500;">$1</strong>',
                                    ),
                                  }}
                                />
                              )}
                            </div>

                            {/* ドメイン（右揃え・リンク）またはprovided_infoラベル */}
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                marginTop: 8,
                              }}
                            >
                              {isProvidedInfo ? (
                                <Tag color="purple" style={{ margin: 0 }}>
                                  独自情報
                                </Tag>
                              ) : citationUrl ? (
                                <a
                                  href={citationUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="citation-domain-link"
                                  style={{
                                    fontSize: 12,
                                    color: '#8c8c8c',
                                    textDecoration: 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 4,
                                    transition: 'color 0.2s ease',
                                  }}
                                >
                                  <LinkOutlined style={{ fontSize: 11 }} />
                                  <span>{extractDomain(citationUrl)}</span>
                                </a>
                              ) : (
                                <span
                                  style={{
                                    fontSize: 12,
                                    color: '#bfbfbf',
                                    fontStyle: 'italic',
                                  }}
                                >
                                  ソース不明
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      {/* プロダクトツアー */}
      <EnhancedTour
        open={tourOpen}
        steps={tourSteps}
        onClose={completeTour}
        onFinish={completeTour}
        onSkip={() => skipTour()}
        current={currentStep}
        onChange={updateCurrentStep}
        tourTitle="STEP4.4: 最終調整"
      />
    </div>
  );
};

export default Step4_4_FinalAdjustment;
